import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { text as textColors } from 'theme/palette'
import typography, { fontWeight } from 'theme/typography'
import { HashLink as Link } from 'react-router-hash-link'
import Type from 'components/Type'
import { HOST, BASE_URL } from 'utils/constants'
import { useScript, useBreakpoints } from 'utils/hooks'

const StyledA = styled.a`
  ${props => (props.to || props.href ? 'cursor: pointer;' : '')}
  ${props => (props.disabled ? 'pointer-events: none;' : '')}
  ${props => (props.wrap ? 'display: inline-grid;' : '')}
  ${props => (props.block ? 'display: grid;' : '')}
`

const A = props => {
  const updateReady = useSelector(({ app }) => app.updateReady) || {}
  const isExternal = !HOST.includes(location.hostname)
  const isMail = !!props.href?.includes('mailto')
  let { to } = props
  const prepend = isExternal && !isMail ? HOST : ''
  let href = `${prepend}${isExternal && to ? to : props.href || ''}`
  const target = to ? '_self' : isMail ? '_blank' : '_blank'
  let component = !!to && !props.disabled && !isExternal ? Link : 'a'
  const { md } = useBreakpoints()

  const scrollWithOffset = (el) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset
    const yOffset = md ? -90 : -70
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' }) 
}

  if (isExternal) {
    href = props.to ? `${BASE_URL}${props.to}` : href
    to = undefined
    component = 'a'
  }

  if (props.disabled) to = href = undefined

  useScript('https://unpkg.com/smoothscroll-polyfill@0.4.4/dist/smoothscroll.min.js')

  if (!to?.length && !href?.length) {
    return (
      <span
        style={{
          display: 'contents',
        }}
        onClick={e => props.onClick?.(e)}
      >
        {props.children}
      </span>
    )
  }

  const renderA = () => (
    <StyledA
      as={component}
      to={to}
      href={href}
      target={target}
      smooth
      line={props.line ? 'true' : undefined}
      disabled={props.disabled}
      download={props.download}
      wrap={props.wrap ? 'true' : undefined}
      block={props.block ? 'true' : undefined}
      scroll={el => scrollWithOffset(el)}
      onClick={e => {
        props.onClick?.(e)
        if (updateReady.status === 'updated' && to) {
          e.preventDefault()
          return (window.location = to)
        }
      }}
    >
      {props.children}
    </StyledA>
  )

  return props.wrapComponent ? (
    renderA()
  ) : (
    <Type
      variant={props.variant}
      color={props.color}
      weight={props.weight ? props.weight : undefined}
      wrap={props.wrap}
      textDecoration={props.decoration || props.textDecoration}
      hover={props.hover}
    >
      {renderA()}
    </Type>
  )
}

A.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  color: PropTypes.oneOf(Object.keys(textColors)),
  variant: PropTypes.oneOf(Object.keys(typography)),
  weight: PropTypes.oneOf(Object.keys(fontWeight)),
  line: PropTypes.bool,
  wrap: PropTypes.string,
  textDecoration: PropTypes.string,
  disabled: PropTypes.bool,
  hover: PropTypes.bool,
  absolute: PropTypes.bool,
  jump: PropTypes.bool,
}

A.defaultProps = {
  color: 'inherit',
  variant: 'link',
  line: false,
}

export default A
